/*******************
Drop Down Navigation
********************/
export default function dropDownNavigation() {

    const triggers = document.querySelectorAll('.navigation-list > li.has-descendants');
    const background = document.querySelector('.dropdown-background');

    /**
     * Pill positioning uses a nav root rect. Base themes use .navigation on <nav>;
     * some client themes (e.g. Loftco) omit that class because .navigation has legacy
     * mobile display:none rules. Fall back to the <nav> wrapping .navigation-list.
     */
    function getNavigationRoot() {
        return (
            document.querySelector('.navigation')
            ?? document.querySelector('ul.navigation-list')?.closest('nav')
            ?? null
        );
    }

    let flyoutCloseTimeout = null;
    let flyoutOpenTimeout = null;
    let currentFlyoutTrigger = null;

    function getTriggerElement(trigger) {
        return trigger.querySelector('[aria-haspopup="true"]') || trigger;
    }

    function positionFlyout(trigger, dropdown) {
        const triangle = dropdown.querySelector('[data-flyout-triangle]');
        if (!triangle) return;
        const triggerEl = getTriggerElement(trigger);
        const triggerRect = triggerEl.getBoundingClientRect();
        const triggerCenterX = triggerRect.left + triggerRect.width / 2;
        const dropdownRect = dropdown.getBoundingClientRect();
        const arrowHalfWidth = 10;
        const centerOffset = triggerCenterX - dropdownRect.left;
        const min = arrowHalfWidth;
        const max = Math.max(min, dropdownRect.width - arrowHalfWidth);
        const left = Math.min(Math.max(centerOffset - arrowHalfWidth, min), max);
        triangle.style.left = `${left}px`;
    }

    function positionBackgroundArrow(trigger, dropdown) {
        if (!background) return;
        const arrow = background.querySelector('.arrow');
        if (!arrow) return;
        const triggerEl = getTriggerElement(trigger);
        const triggerRect = triggerEl.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();
        const triggerCenterX = triggerRect.left + (triggerRect.width / 2);
        const centerOffset = triggerCenterX - dropdownRect.left;
        const arrowHalfWidth = 10;
        const min = arrowHalfWidth;
        const max = Math.max(min, dropdownRect.width - arrowHalfWidth);
        const left = Math.min(Math.max(centerOffset - arrowHalfWidth, min), max);
        arrow.style.left = `${left}px`;
    }

    function closeFlyout(immediate = false) {
        if (flyoutCloseTimeout) {
            clearTimeout(flyoutCloseTimeout);
            flyoutCloseTimeout = null;
        }
        if (flyoutOpenTimeout) {
            clearTimeout(flyoutOpenTimeout);
            flyoutOpenTimeout = null;
        }
        const panel = document.body.querySelector('.dropdown[data-flyout-panel]');
        if (panel && panel._flyoutParent) {
            const triggerLi = panel._flyoutParent;
            const doRemove = () => {
                if (!panel._flyoutParent) return;
                panel._flyoutParent.appendChild(panel);
                panel._flyoutParent = null;
                panel.removeAttribute('style');
                panel.style.cssText = '';
                panel.classList.remove('dropdown--flyout-visible');
                panel.removeEventListener('mouseenter', panel._onFlyoutEnter);
                panel.removeEventListener('mouseleave', panel._onFlyoutLeave);
                panel.removeEventListener('transitionend', panel._onFlyoutTransitionEnd);
                if (triggerLi && triggerLi.classList) triggerLi.classList.remove('trigger-enter', 'trigger-enter-active');
                if (background) background.classList.remove('open');
                currentFlyoutTrigger = null;
            };
            if (!immediate && panel.style.opacity === '1' && panel.style.transition) {
                panel._onFlyoutTransitionEnd = (e) => {
                    if (e.target === panel && e.propertyName === 'opacity') doRemove();
                };
                panel.addEventListener('transitionend', panel._onFlyoutTransitionEnd);
                panel.style.opacity = '0';
            } else {
                doRemove();
            }
        } else if (background) {
            background.classList.remove('open');
            currentFlyoutTrigger = null;
        }
    }

    function handleEnter() {
        if (flyoutCloseTimeout) {
            clearTimeout(flyoutCloseTimeout);
            flyoutCloseTimeout = null;
        }
        if (flyoutOpenTimeout) {
            clearTimeout(flyoutOpenTimeout);
            flyoutOpenTimeout = null;
        }
        if (currentFlyoutTrigger && currentFlyoutTrigger !== this) {
            closeFlyout(true);
        }
        // Clean up any other open dropdowns (regular non-flyout ones)
        triggers.forEach(t => {
            if (t !== this && (t.classList.contains('trigger-enter') || t.classList.contains('trigger-enter-active'))) {
                t.classList.remove('trigger-enter', 'trigger-enter-active');
            }
        });
        if (background) background.classList.remove('open');
        
        this.classList.add('trigger-enter');
        setTimeout(() => this.classList.contains('trigger-enter') && this.classList.add('trigger-enter-active'), 50);

        const dropdown = this.querySelector('.dropdown');
        if (!dropdown) return;

        const navigation = getNavigationRoot();
        const isFlyoutPanel = dropdown.hasAttribute('data-flyout-panel');

        // Full-width flyout panels only need the trigger <li>; legacy pill dropdowns need a nav root.
        if (!navigation && !isFlyoutPanel) return;

        const navigationCoords = navigation ? navigation.getBoundingClientRect() : null;

        // Only show dropdown-background (sliding pill) if:
        // 1. Not a fullwidth flyout panel
        // 2. Dropdown doesn't have data-no-pill attribute (has its own styled container)
        const noPill = dropdown.hasAttribute('data-no-pill');
        if (isFlyoutPanel || noPill) {
            // Don't show dropdown-background for fullwidth flyouts or dropdowns with own styling
        } else if (background) {
            background.classList.add('open');
        }

        if (isFlyoutPanel) {
            const triggerEl = getTriggerElement(this);
            const triggerRect = triggerEl.getBoundingClientRect();
            dropdown._flyoutParent = dropdown.parentNode;
            document.body.appendChild(dropdown);
            dropdown.classList.add('dropdown--flyout-visible');

            // Position from the trigger instead of the hidden dropdown's rect.
            // On first hover the panel is still hidden, so its rect is unreliable.
            dropdown.style.position = 'fixed';
            dropdown.style.left = '0';
            dropdown.style.right = '0';
            dropdown.style.width = '100%';
            dropdown.style.top = `${triggerRect.bottom + 12}px`;
            dropdown.style.zIndex = '50';
            dropdown.style.maxWidth = 'none';
            dropdown.style.transform = 'none';
            dropdown.style.display = 'block';
            dropdown.style.visibility = 'visible';
            dropdown.style.transition = 'opacity 0.15s';
            dropdown.style.opacity = '0';
            dropdown.removeAttribute('hidden');

            dropdown._onFlyoutEnter = () => {
                if (flyoutCloseTimeout) {
                    clearTimeout(flyoutCloseTimeout);
                    flyoutCloseTimeout = null;
                }
            };
            dropdown._onFlyoutLeave = () => {
                closeFlyout(false);
            };
            dropdown.addEventListener('mouseenter', dropdown._onFlyoutEnter);
            dropdown.addEventListener('mouseleave', dropdown._onFlyoutLeave);
            currentFlyoutTrigger = this;

            requestAnimationFrame(() => {
                if (!dropdown._flyoutParent) return;
                positionFlyout(this, dropdown);
            });

            flyoutOpenTimeout = setTimeout(() => {
                if (dropdown._flyoutParent) dropdown.style.opacity = '1';
            }, 50);
        } else if (navigationCoords && background) {
            const dropdownCoords = dropdown.getBoundingClientRect();
            const coords = {
                height: dropdownCoords.height,
                width: dropdownCoords.width,
                top: dropdownCoords.top - navigationCoords.top,
                left: dropdownCoords.left - navigationCoords.left,
            };
            background.style.setProperty('width', `${coords.width}px`);
            background.style.setProperty('height', `${coords.height}px`);
            background.style.setProperty('transform', `translate(${coords.left}px, ${coords.top}px)`);
            positionBackgroundArrow(this, dropdown);
        }
    }

    function handleLeave() {
        const trigger = this;
        
        // Check if there's a flyout panel in body that belongs to this trigger
        const bodyFlyout = document.body.querySelector('.dropdown[data-flyout-panel]');
        if (bodyFlyout && bodyFlyout._flyoutParent === trigger) {
            // Flyout is open and belongs to this trigger - start close timeout
            flyoutCloseTimeout = setTimeout(() => closeFlyout(false), 150);
            return;
        }
        
        const dropdown = this.querySelector('.dropdown');
        if (!dropdown) return; // Exit early on mobile or if no dropdown exists
        const isFlyoutPanel = dropdown.hasAttribute('data-flyout-panel');

        if (isFlyoutPanel && dropdown.parentNode === document.body) {
            currentFlyoutTrigger = this;
            flyoutCloseTimeout = setTimeout(() => closeFlyout(false), 150);
        } else {
            // Add delay for regular dropdowns too
            flyoutCloseTimeout = setTimeout(() => {
                trigger.classList.remove('trigger-enter', 'trigger-enter-active');
                if (background) background.classList.remove('open');
                if (dropdown.hasAttribute('data-flyout-panel') && dropdown.parentNode !== document.body) {
                    dropdown.style.cssText = '';
                    dropdown.removeAttribute('style');
                }
            }, 150);
        }
    }

    triggers.forEach(trigger => trigger.addEventListener('mouseenter', handleEnter));
    triggers.forEach(trigger => trigger.addEventListener('mouseleave', handleLeave));
}
